<!--
  CalDialogShell: common frame of the course dialogs in the new style (same building blocks as the signup-board
  dialogs): header with soft avatar, title, subtitle and close, scrolling body, footer with cancel + primary action.
  Fullscreen on phones, not closable while loading. Only presentation: the dialogs keep their own logic.
  Props: value, title, subtitle, icon, tone (info | warn | bad | good | teal | muted), loading, initializing (spinner
         instead of the body), maxWidth, saveText, saveIcon, saveDisabled, saveDisabledReason (tooltip), danger (red primary button), hideSave, hideCancel, cancelText,
         persistent, closeOnEsc (persistent dialog that still closes with Escape)
  Slots: default (body), footer-start (left of the buttons), footer-actions (extra buttons left of the primary one), title-extra (next to the title)
  Events: input (false = close), save
-->
<template>
  <v-dialog ref="dialog" :fullscreen="$vuetify.breakpoint.smAndDown" :max-width="maxWidth" :persistent="loading || persistent"
            :value="value" content-class="acq-dialog" scrollable @input="close" @keydown="onKeydown">
    <v-card class="acq-dialog mdlg-scope d-flex flex-column">
      <div class="mdlg-header">
        <span :class="`tone-${tone}`" class="acq-avatar acq-avatar--sm"><v-icon small style="color: inherit">{{ icon }}</v-icon></span>
        <div class="mdlg-header__text">
          <div class="acq-section-title">{{ title }}<slot name="title-extra"/></div>
          <div v-if="subtitle" class="acq-tiny">{{ subtitle }}</div>
        </div>
        <button :disabled="loading" aria-label="Schließen" class="cta cta--ghost" type="button" @click="close">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>
      <v-card-text class="mdlg-body">
        <div v-if="initializing" class="cdlg-empty"><v-progress-circular :color="spinnerColor" indeterminate/></div>
        <slot v-else/>
      </v-card-text>
      <div v-if="!hideSave || !hideCancel" class="mdlg-footer">
        <slot name="footer-start"/>
        <v-spacer/>
        <button v-if="!hideCancel" :disabled="loading" class="cta cta--ghost" type="button" @click="close">{{ cancelText }}</button>
        <slot name="footer-actions"/>
        <span v-if="!hideSave" v-tip="saveDisabled && !loading && !initializing ? saveDisabledReason : ''" class="app-tip-wrap">
          <button :class="danger ? 'cta--danger' : 'cta--primary'" :disabled="loading || initializing || saveDisabled" class="cta"
                  type="button" @click="$emit('save')">
            <v-progress-circular v-if="loading" class="cta__icon" indeterminate size="14" width="2"/>
            <v-icon v-else-if="saveIcon" class="cta__icon" small>{{ saveIcon }}</v-icon>
            {{ saveText }}
          </button>
        </span>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';

export default Vue.extend({
  props: {
    /** Forms: a click outside must not discard what was typed (closing only via buttons) */
    persistent: {type: Boolean, default: false},
    /** With persistent: Escape still closes (only a click outside is ignored) */
    closeOnEsc: {type: Boolean, default: false},
    value: {type: Boolean, required: true},
    title: {type: String, required: true},
    subtitle: {type: String, default: ''},
    icon: {type: String, default: 'mdi-pencil-outline'},
    tone: {type: String, default: 'info'},
    loading: {type: Boolean, default: false},
    initializing: {type: Boolean, default: false},
    maxWidth: {type: [Number, String], default: 560},
    saveText: {type: String, default: 'Speichern'},
    saveIcon: {type: String, default: 'mdi-check'},
    saveDisabled: {type: Boolean, default: false},
    /** Tooltip on the disabled primary button: why it cannot be clicked yet */
    saveDisabledReason: {type: String, default: ''},
    danger: {type: Boolean, default: false},
    hideSave: {type: Boolean, default: false},
    cancelText: {type: String, default: 'Abbrechen'},
    /** No cancel button (with hideSave: no footer at all – the body owns its actions) */
    hideCancel: {type: Boolean, default: false},
  },
  computed: {
    spinnerColor(): string {
      return ({warn: 'warning', bad: 'error', good: 'success'} as Record<string, string>)[this.tone] || 'info';
    },
  },
  methods: {
    /** Vuetify ignores Escape on persistent dialogs; closeOnEsc closes unless a menu/picker inside is open */
    onKeydown(e: KeyboardEvent) {
      if (!this.closeOnEsc || e.key !== 'Escape') {
        return;
      }
      const dialog = this.$refs.dialog as unknown as { getOpenDependents?: () => unknown[] } | undefined;
      if (!dialog?.getOpenDependents?.().length) {
        this.close();
      }
    },
    close() {
      if (!this.loading) {
        this.$emit('input', false);
      }
    },
  },
});
</script>

<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>
<style src="./calDialog.css"></style>
